import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { plaudApi, type PlaudIntegrationStatus } from '@/services/api/plaud';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

type Tone = 'green' | 'amber' | 'red' | 'neutral';

const TONE_CLASSES: Record<Tone, string> = {
  green:
    'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/20 dark:text-green-300 dark:ring-green-400/20',
  amber:
    'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-400/20',
  red: 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-400/20',
  neutral:
    'bg-neutral-100 text-neutral-600 ring-neutral-500/20 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-400/15',
};

function CountBadge({ value, label, tone }: { value: number; label: string; tone: Tone }): JSX.Element {
  const t = value > 0 ? tone : 'neutral';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${TONE_CLASSES[t]}`}
    >
      <span className="tabular-nums font-semibold">{value}</span>
      {label}
    </span>
  );
}

const CODE_CHIP =
  'rounded bg-neutral-100 px-1 py-0.5 font-mono text-[0.8em] text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300';

export function PlaudIntegrationPanel(): JSX.Element {
  const [status, setStatus] = useState<PlaudIntegrationStatus | null>(null);
  const [bridgePresent, setBridgePresent] = useState(false);
  const [pasteValue, setPasteValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isAdopting, setIsAdopting] = useState(false);

  async function refresh(): Promise<void> {
    const [s, b] = await Promise.all([plaudApi.getStatus(), plaudApi.getBridgePresent()]);
    setStatus(s);
    setBridgePresent(b);
    setLoadError(null);
  }

  useEffect(() => {
    refresh().catch((e: unknown) => {
      setLoadError(e instanceof Error ? e.message : String(e));
    });
    const id = setInterval(() => {
      // Swallow background refresh errors; the initial-load error UI covers hard failures.
      void refresh().catch(() => undefined);
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  async function submitPasted(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const refreshToken = extractRefreshToken(pasteValue);
      if (!refreshToken) {
        setError('Could not find a refresh_token in the pasted content.');
        return;
      }
      await plaudApi.submitTokens(refreshToken);
      setPasteValue('');
      await refresh();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function adoptUnmatched(): Promise<void> {
    setIsAdopting(true);
    try {
      await plaudApi.adoptUnmatched();
      toast.success('Unmatched recordings are being transcribed, each in its own meeting.');
      await refresh();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to queue unmatched recordings.');
    } finally {
      setIsAdopting(false);
    }
  }

  async function disconnect(): Promise<void> {
    try {
      await plaudApi.disconnect();
      await refresh();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to disconnect Plaud.');
    }
  }

  if (status === null) {
    if (loadError !== null) {
      return (
        <div className="space-y-3">
          <p className="flex items-start gap-1.5 text-sm text-red-600 dark:text-red-400">
            <svg
              className="mt-0.5 h-4 w-4 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Could not load the Plaud integration status. {loadError}
          </p>
          <button
            onClick={() => {
              refresh().catch((e: unknown) => {
                setLoadError(e instanceof Error ? e.message : String(e));
              });
            }}
            className="inline-flex items-center justify-center rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-800"
          >
            Retry
          </button>
        </div>
      );
    }
    return <div className="text-sm text-neutral-400 dark:text-neutral-500">Loading…</div>;
  }

  if (status.connected) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
          </span>
          <span className="text-sm font-medium text-neutral-900 dark:text-white">
            Connected as{' '}
            <span className="text-green-600 dark:text-green-400">{status.plaud_email}</span>
          </span>
        </div>

        <div className="flex flex-wrap gap-2">
          <CountBadge value={status.matched_count} label="matched" tone="green" />
          <CountBadge value={status.unmatched_count} label="unmatched" tone="amber" />
          <CountBadge value={status.failed_count} label="failed" tone="red" />
        </div>

        <div className="flex items-center gap-4 pt-1">
          {status.unmatched_count > 0 && (
            <Link
              to="/settings/integrations/plaud/unmatched"
              className="text-sm font-medium text-green-600 transition-colors hover:text-green-500 dark:text-green-400 dark:hover:text-green-300"
            >
              Review unmatched recordings →
            </Link>
          )}
          {status.unmatched_count > 0 && (
            <button
              onClick={() => {
                void adoptUnmatched();
              }}
              disabled={isAdopting}
              className="text-sm font-medium text-green-600 transition-colors hover:text-green-500 disabled:opacity-50 dark:text-green-400 dark:hover:text-green-300"
            >
              {isAdopting ? 'Queuing…' : 'Transcribe unmatched recordings'}
            </button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="text-sm font-medium text-red-600 transition-colors hover:text-red-500 dark:text-red-400 dark:hover:text-red-300">
                Disconnect
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Disconnect Plaud?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will revoke access and remove the integration.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    void disconnect();
                  }}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  Disconnect
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
        Connect your Plaud account
      </h3>
      {bridgePresent ? (
        <p className="text-sm leading-relaxed text-neutral-500 dark:text-neutral-400">
          NeoKanban Bridge is running. Open{' '}
          <strong className="font-medium text-neutral-700 dark:text-neutral-200">
            Bridge → Preferences → Plaud
          </strong>{' '}
          and click{' '}
          <strong className="font-medium text-green-600 dark:text-green-400">Connect Plaud</strong>.
          This panel updates automatically.
        </p>
      ) : (
        <>
          <p className="text-sm leading-relaxed text-neutral-500 dark:text-neutral-400">
            To connect Plaud, install the official MCP and authenticate:
          </p>
          <pre className="overflow-x-auto rounded-lg border border-neutral-200 bg-neutral-50 p-3 font-mono text-xs leading-relaxed text-neutral-700 dark:border-neutral-700/70 dark:bg-neutral-900/60 dark:text-neutral-300">
            {`npm install -g @plaud-ai/mcp
plaud-mcp login`}
          </pre>
          <p className="text-sm leading-relaxed text-neutral-500 dark:text-neutral-400">
            Then paste the contents of <code className={CODE_CHIP}>~/.plaud/tokens-mcp.json</code> (or
            just the <code className={CODE_CHIP}>refresh_token</code>) below:
          </p>
          <textarea
            value={pasteValue}
            onChange={(e) => setPasteValue(e.target.value)}
            rows={4}
            placeholder='{"refresh_token":"…"} or just the refresh_token string'
            className="block w-full rounded-md border border-neutral-300 bg-white p-2.5 font-mono text-xs text-neutral-900 shadow-sm transition-colors placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-900/50 dark:text-white dark:placeholder:text-neutral-500"
          />
          {error && (
            <p className="flex items-start gap-1.5 text-sm text-red-600 dark:text-red-400">
              <svg
                className="mt-0.5 h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {error}
            </p>
          )}
          <button
            onClick={() => {
              void submitPasted();
            }}
            disabled={submitting || pasteValue.length < 10}
            className="inline-flex items-center justify-center rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-neutral-900"
          >
            {submitting ? 'Connecting…' : 'Connect Plaud'}
          </button>
        </>
      )}
    </div>
  );
}

function extractRefreshToken(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        'refresh_token' in parsed &&
        typeof (parsed as Record<string, unknown>)['refresh_token'] === 'string'
      ) {
        return (parsed as Record<string, string>)['refresh_token'] ?? null;
      }
      return null;
    } catch {
      return null;
    }
  }
  return trimmed.length > 10 ? trimmed : null;
}
